/**
 * onboarding-setup — pure decision seam for the first-run setup wizard
 * (`/setup`). Answers three questions with no I/O so vitest can pin them:
 *
 *   1. `needsSelfServeSetup` — should this signed-in user be routed into the
 *      wizard (and shown the "finish setting up" banner elsewhere)?
 *   2. `pickSetupTenant` — which tenant is the wizard driving?
 *   3. `composeRelayEmail` — the pre-written email a customer forwards to
 *      their Microsoft 365 admin when they aren't the one who can run the
 *      setup script (the Lighthouse "copy email to clipboard" pattern).
 *
 * Eligibility is deliberately narrow: exactly one accessible tenant, born of
 * self-service signup (`onboardingStatus === 'SelfService'`), not yet
 * promoted (`baselineSource !== 'Hybrid'`), for a non-platform-admin user.
 * Fleet tenants (onboardingStatus 'Manual'/'Migrated'), MSP users with many
 * tenants, and iuvo operators never see the wizard. Once the server-side
 * finish promotes the tenant, the predicate flips false on the next tenants
 * refetch and the wizard/banner disappear — the standing health surface is
 * Baseline Compliance → Monitoring.
 */

import type { Tenant } from '@/types'

export const SETUP_ROUTE = '/setup'

/** The slice of the current-user payload the predicate needs. */
export interface SetupGateUser {
  isPlatformAdmin?: boolean
}

/**
 * The wizard's target tenant: the caller's single self-service tenant that
 * has not yet reached its promoted steady state. Null when no tenant
 * qualifies (wrong lifecycle, several tenants, none at all).
 */
export function pickSetupTenant(tenants: Tenant[] | undefined): Tenant | null {
  if (!tenants || tenants.length !== 1) return null
  const only = tenants[0]
  if (only.onboardingStatus !== 'SelfService') return null
  if (only.baselineSource === 'Hybrid') return null
  if (!only.isActive) return null
  return only
}

export function needsSelfServeSetup(
  user: SetupGateUser | null | undefined,
  tenants: Tenant[] | undefined,
): boolean {
  if (!user || user.isPlatformAdmin === true) return false
  return pickSetupTenant(tenants) !== null
}

// Auto-route fire-once flag. Module-scoped (not a component ref) because
// Layout unmounts when the app hands off to /setup and remounts when the
// user comes back via "skip for now" — a ref would reset and re-trap them.
let setupAutoRouted = false

/**
 * Consume the one per-SPA-session auto-route into `/setup`. Returns true
 * exactly once; every later call returns false, so a user who skipped the
 * wizard keeps free navigation (the banner carries the reminder).
 */
export function consumeSetupAutoRoute(): boolean {
  if (setupAutoRouted) return false
  setupAutoRouted = true
  return true
}

/** Test-only reset for the module-scoped fire-once flag. */
export function resetSetupAutoRouteForTests(): void {
  setupAutoRouted = false
}

/**
 * Should a coverage poll that just saw `baselineSource === 'Hybrid'` refresh
 * the ['tenants'] cache? Only on the actual transition — when the cached
 * tenant row still says non-Hybrid (its 5-minute staleTime would otherwise
 * keep the "Setup isn't finished" banner alive after setup completed, codex
 * P2). Settled fleet tenants (cache already Hybrid) must be a no-op, or
 * every Monitoring-tab visit would churn a tenants refetch.
 */
export function tenantsCacheNeedsRefresh(
  summaryBaselineSource: string | null,
  cachedTenants: Tenant[] | undefined,
  tenantId: string,
): boolean {
  if (summaryBaselineSource !== 'Hybrid') return false
  const cached = cachedTenants?.find(t => t.tenantId === tenantId)
  return cached !== undefined && cached.baselineSource !== 'Hybrid'
}

/**
 * Copy for the persistent Layout banner while setup is unfinished. Kept
 * honest across every pre-promoted state (including the server-side
 * finishing window, where there is nothing left for the customer to do) —
 * the guide itself says exactly where things stand.
 */
export const SETUP_BANNER = {
  headline: 'Setup isn’t finished.',
  body: 'The setup guide shows exactly where things stand.',
  cta: 'Open the guide',
} as const

/**
 * Pre-written relay email for the "I'm not the Microsoft 365 admin" case.
 * Conceptual copy only — no internal pipeline or vendor tool names reach the
 * customer (feedback_end_user_docs_generic).
 */
export function composeRelayEmail(scriptFilename: string): {
  subject: string
  body: string
} {
  return {
    subject: 'Guardian setup: one setup script to run (about 10 minutes)',
    body: [
      'Hi,',
      '',
      'We’re connecting our Microsoft 365 environment to Guardian, the service that watches our configuration for risky changes. One setup step needs a Global Administrator.',
      '',
      `1. I’ll send you the setup script (${scriptFilename}). It was generated specifically for our organization and is good for 24 hours.`,
      '2. Run it in PowerShell on your machine, signed in as a Global Administrator. It sets up the enterprise apps Guardian needs in our tenant and grants them roles and permissions, most of them read-only, listed at https://guardian.iuvotech.com/docs/permissions. It doesn’t change our policies or settings, apart from turning on Exchange organization customization if it’s off, which Exchange role assignments need, and removing the Compliance Administrator role from Microsoft’s M365 Tenant Configuration Manager app unless our setup includes Purview compliance monitoring.',
      '3. When it finishes, it prints a block of text at the end. Copy that whole block and send it back to me, and I’ll paste it into Guardian to finish the connection.',
      '',
      'The script is readable top to bottom if you’d like to review it first. Thanks.',
    ].join('\n'),
  }
}
